import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./DateField.css";

// "YYYY-MM-DD" → Date local a medianoche (evita el corrimiento de día por tz).
function parseDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const [y, m, d] = String(v).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function toISO(d) {
  if (!d) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function toDisplay(d) {
  if (!d) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const CAL_WIDTH = 300; // debe coincidir con el ancho en DateField.css

/**
 * Date picker estándar de la app. Réplica de la experiencia de Kapella:
 * el calendario permite navegar por grillas de día → mes → año (clic en el
 * título) y viajar años/décadas con las flechas « ». El valor y el onChange
 * viajan en formato "YYYY-MM-DD" (compatible con el backend).
 */
function DateField({
  id,
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder = "dd/mm/aaaa",
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const selected = parseDate(value);

  const reposition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Mantén el popover dentro de la ventana (evita que se salga por la derecha).
    const left = Math.max(8, Math.min(r.left, window.innerWidth - CAL_WIDTH - 24));
    setPos({ top: r.bottom + 4, left });
  }, []);

  const openCal = () => {
    if (disabled) return;
    reposition();
    setOpen(true);
  };

  // Cierra al hacer clic fuera, con Escape, o al hacer scroll/resize (el popover
  // usa position: fixed y no sigue el desplazamiento).
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        wrapRef.current &&
        !wrapRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  const handleChange = (d) => {
    onChange(toISO(Array.isArray(d) ? d[0] : d));
    setOpen(false);
  };

  return (
    <div className="date-field" ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={`date-field__control form-control d-flex align-items-center text-start${
          disabled ? " disabled" : ""
        }`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={openCal}
      >
        <span
          className={`flex-grow-1 text-truncate${selected ? "" : " text-muted"}`}
        >
          {selected ? toDisplay(selected) : placeholder}
        </span>
        <i
          className="mdi mdi-calendar-blank-outline ms-2 text-muted"
          aria-hidden="true"
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={popRef}
            className="date-field__popover"
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 3000 }}
          >
            <Calendar
              onChange={handleChange}
              value={selected}
              locale="es-ES"
              minDate={parseDate(min)}
              maxDate={parseDate(max)}
              minDetail="decade"
              prevLabel="«"
              nextLabel="»"
              prev2Label={null}
              next2Label={null}
              formatShortWeekday={(locale, date) => {
                const s = date.toLocaleDateString("es-ES", { weekday: "short" });
                return s.charAt(0).toUpperCase() + s.slice(1, 2);
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
}

export { DateField };
